import type { SiteType } from './types';

/**
 * Site pins for the Google map: a circle for a pit, a rounded square for a yard, a triangle for
 * a rockfall route - the same shapes TypeMark draws in the list and legend.
 *
 * Google draws a marker from an image, so CSS cannot reach it. The colours therefore live here,
 * as the one copy, and TypeMark draws the list's small type marks from the same values.
 */

export const PIN_FILL: Record<SiteType, string> = {
  pit: '#ff8a00',
  yard: '#1d4ed8',
  rockfall: '#475569',
};
const PIN_STROKE = '#0f172a';

const SHAPE: Record<SiteType, string> = {
  pit: '<circle cx="12" cy="12" r="7.2" />',
  yard: '<rect x="5" y="5" width="14" height="14" rx="3.2" />',
  rockfall: '<polygon points="12,3.2 21.2,20.2 2.8,20.2" />',
};

export interface MarkerState {
  selected: boolean;
  hovered: boolean;
  empty: boolean;
  dimmed: boolean;
}

/** Pixel size of a pin: larger while selected or hovered. */
export function pinSize(state: MarkerState): number {
  return state.selected || state.hovered ? 34 : 26;
}

/** The pin's SVG markup. Exported for tests; the map uses siteIcon. */
export function pinSvg(type: SiteType, state: MarkerState): string {
  const strokeWidth = state.selected || state.hovered ? 3.4 : 2.8;
  const fillOpacity = state.empty ? 0.85 : 1;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-2 -2 28 28">` +
    `<defs><filter id="s" x="-50%" y="-50%" width="200%" height="200%">` +
    `<feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#0f202b" flood-opacity="0.45"/></filter></defs>` +
    `<g filter="url(#s)" fill="${PIN_FILL[type]}" fill-opacity="${fillOpacity}" stroke="${PIN_STROKE}" ` +
    `stroke-width="${strokeWidth}" stroke-linejoin="round" paint-order="stroke fill">${SHAPE[type]}</g></svg>`
  );
}

export const svgDataUrl = (svg: string): string => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

/** A google.maps.Icon for a site pin, anchored at its centre. */
export function siteIcon(type: SiteType, state: MarkerState): google.maps.Icon {
  const size = pinSize(state);
  return {
    url: svgDataUrl(pinSvg(type, state)),
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2),
  };
}

/** The cluster bubble: dark disc, light ring. The count is drawn on top by the clusterer. */
export function clusterIconUrl(): string {
  return svgDataUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">` +
      `<circle cx="16" cy="16" r="14.5" fill="#14202b" stroke="#ffffff" stroke-width="2"/></svg>`
  );
}
